import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  CepLookupResult,
  CompanyAddressDto,
  CompanyAddressInput,
  CompanyAddressListItem,
} from './types';

/**
 * Fábrica do CRUD aninhado em uma Empresa. `companyId` fica fechado no escopo
 * — espelha o pattern de `createClientProjectsApi` em `clientes/projects/api.ts`.
 *
 * Endpoint adicional não-CRUD: `lookupCep` chama `GET .../addresses/lookup?cep=`.
 */
export function createCompanyAddressesApi(companyId: number): CrudApi<
  CompanyAddressListItem,
  CompanyAddressDto,
  CompanyAddressInput
> & {
  lookupCep: (cep: string) => Promise<CepLookupResult>;
} {
  const BASE = `/v1/companies/${companyId}/addresses`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<CompanyAddressListItem>> {
      const { data } = await api.get<{
        data: CompanyAddressListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      return normalizeListEnvelope(data);
    },

    async get(id: number): Promise<CompanyAddressDto> {
      const { data } = await api.get<{ data: CompanyAddressDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async create(input: CompanyAddressInput): Promise<CompanyAddressDto> {
      const { data } = await api.post<{ data: CompanyAddressDto }>(BASE, input);
      return data.data;
    },

    async update(id: number, input: CompanyAddressInput): Promise<CompanyAddressDto> {
      const { data } = await api.put<{ data: CompanyAddressDto }>(`${BASE}/${id}`, input);
      return data.data;
    },

    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },

    async lookupCep(cep: string): Promise<CepLookupResult> {
      const digits = cep.replace(/\D/g, '');
      const { data } = await api.get<{ data: CepLookupResult }>(`${BASE}/lookup`, {
        params: { cep: digits },
      });
      return data.data;
    },
  };
}
